import { Auth } from '@aws-amplify/auth';
import { Cache } from '@aws-amplify/cache';
import { FeatureFlagResponseData } from '../interfaces/featureFlags';
import { checkFeature } from './featureFlag';
import handleSupergraphRequest from './handleSupergraphRequest';
export const USE_LOCAL_SCHEDULING_ASSISTANT: boolean =
    process.env.REACT_APP_USE_LOCAL_SCHEDULING_ASSISTANT === 'true';
export const SCHEDULING_ASSISTANT_AGENT_NAME = 'grimsby-scheduling';
export const SCHEDULING_ASSISTANT_FEATURE = 'scheduling-assistant';
export const SCHEDULING_ASSISTANT_EXTERNAL_REFERENCE = 'externalReferenceId';

type SchedulingAssistantStage = 'beta' | 'gamma' | 'prod';
export type SchedulingAssistantTarget = 'local' | SchedulingAssistantStage;

const SCHEDULING_ASSISTANT_REALTIME_URLS: Record<
    SchedulingAssistantStage,
    string
> = {
    beta: 'https://realtime.beta.aigateway.skillbuilder.training.aws.dev/stream',
    gamma: 'https://realtime.gamma.aigateway.skillbuilder.training.aws.dev/stream',
    prod: 'https://realtime.aigateway.skillbuilder.aws/stream',
};

export const getSchedulingAssistantRealtimeUrl = (
    target: SchedulingAssistantTarget,
): string =>
    target === 'local'
        ? 'http://localhost:9000/stream'
        : SCHEDULING_ASSISTANT_REALTIME_URLS[target];

/**
 * Fail CLOSED for the agent surface. `checkFeature` fails open when the
 * feature config or the specific flag entry is missing, but the scheduling
 * assistant must stay dark unless its feature flag is explicitly present.
 */
export const isSchedulingAssistantEnabled = (
    featureFlags: FeatureFlagResponseData | null | undefined,
): boolean => {
    const featureEntry = featureFlags?.features?.[SCHEDULING_ASSISTANT_FEATURE];
    if (!featureEntry) {
        return false;
    }

    return !!checkFeature(
        '',
        { featureName: SCHEDULING_ASSISTANT_FEATURE },
        featureFlags.features,
    );
};

export const getSchedulingAssistantTarget = (
    featureFlags: FeatureFlagResponseData | null | undefined,
): SchedulingAssistantTarget | null => {
    if (!isSchedulingAssistantEnabled(featureFlags)) {
        return null;
    }

    if (USE_LOCAL_SCHEDULING_ASSISTANT) {
        return window.location.hostname === 'localhost' ? 'local' : null;
    }

    const stage = window.__CONFIGURATION__?.STAGE?.toLowerCase();
    return stage &&
        Object.prototype.hasOwnProperty.call(
            SCHEDULING_ASSISTANT_REALTIME_URLS,
            stage,
        )
        ? (stage as SchedulingAssistantStage)
        : null;
};

export const getSchedulingAssistantAuthHeaders = async (): Promise<
    Record<string, string>
> => {
    let token: string | undefined;
    try {
        token = (await Auth.currentSession()).getIdToken().getJwtToken();
    } catch {
        token = Cache.getItem('federatedInfo')?.token;
    }

    if (!token) {
        throw new Error('No Gandalf idToken available — sign in first.');
    }

    const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        'X-Amzn-Bedrock-AgentCore-Runtime-Custom-Tnc-Agent-Name':
            SCHEDULING_ASSISTANT_AGENT_NAME,
    };

    return headers;
};

export const getSchedulingAssistantExternalReferenceId = (
    activityId: string,
): string => `schedulemodal_${activityId}`;

export type SchedulingAssistantConversation = {
    id: string;
    creationTimestamp?: string | null;
    messages?: {
        nodes: Array<{ owner: string; content: string }>;
    };
};

export const decodeSchedulingAssistantMessageContent = (
    content: string,
): string =>
    content.replace(
        /&(amp|lt|gt|quot|#x27|#x2F);/g,
        (entity) =>
            ({
                '&amp;': '&',
                '&lt;': '<',
                '&gt;': '>',
                '&quot;': '"',
                '&#x27;': "'",
                '&#x2F;': '/',
            })[entity] ?? entity,
    );

export const isSchedulingAssistantConversationExpired = (
    conversation: SchedulingAssistantConversation,
): boolean => {
    const creationTimestamp = Date.parse(conversation.creationTimestamp || '');
    return (
        !Number.isFinite(creationTimestamp) ||
        Date.now() - creationTimestamp >= 60 * 60 * 1000
    );
};

export const findSchedulingAssistantConversation = async (
    activityId: string,
    signal?: AbortSignal,
): Promise<SchedulingAssistantConversation | undefined> => {
    const data = await handleSupergraphRequest<{
        listConversations: { nodes: SchedulingAssistantConversation[] };
    }>(
        `query SchedulingAssistantConversations($input: ListConversationsInput!) {
            listConversations(input: $input) {
                nodes {
                    id
                    creationTimestamp
                    messages { nodes { owner content }}
                }
            }
        }`,
        {
            input: {
                clientId: 'skillbuilder',
                externalReferenceId:
                    getSchedulingAssistantExternalReferenceId(activityId),
                pageSize: 1,
            },
        },
        { signal },
    );

    const conversation = data.listConversations.nodes[0];
    if (!conversation?.messages) return conversation;

    return {
        ...conversation,
        messages: {
            nodes: conversation.messages.nodes.map((message) => ({
                ...message,
                content: decodeSchedulingAssistantMessageContent(
                    message.content,
                ),
            })),
        },
    };
};

export const inferSchedulingAssistantRecommendationCount = (
    message: string,
): number => {
    const numberedRecommendations = message.match(/^\d+\\?\.\s+\S.+$/gm);
    if (numberedRecommendations?.length) {
        return numberedRecommendations.length;
    }

    return 0;
};
